import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { HomePageSkeleton } from "../common/home-page-skeleton";
import { AppRoutes } from "../../common/routes/app_routes";
import { useApprovalStatus } from "../../hooks/useApprovalStatus";

interface OnboardingGuardProps {
  children: ReactNode;
}

/**
 * Gates the app on document submission. Buyers without all required documents
 * uploaded are bounced to the documents step of the create-account wizard.
 * Buyers with documents pending review (or approved), sellers, and admins all
 * pass through — the in-app `<ApprovalBanner>` handles the pending state.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { status, isLoading } = useApprovalStatus();

  // In mock mode skip the gate so design walkthroughs aren't blocked by
  // partner/document fixtures.
  if (import.meta.env.VITE_USE_MOCKS === "true") {
    return <>{children}</>;
  }

  if (isLoading) return <HomePageSkeleton />;
  if (status === "unapproved") {
    return <Navigate to={AppRoutes.CREATE_ACCOUNT_DOCUMENTS} replace />;
  }
  return <>{children}</>;
}
